import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

type JsonBody = Record<string, unknown>;

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: "include", ...init });
  if (!res.ok) {
    let errText = "";
    try { errText = await res.text(); } catch {}
    throw new Error(`${res.status}${errText ? `: ${errText}` : ""}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// ─── Models ─────────────────────────────────────────────────────────────

export function useModelPricing() {
  return useQuery({
    queryKey: ["/api/admin/pricing/models"],
    queryFn: () => fetchJson<{ rows: any[] }>("/api/admin/pricing/models"),
  });
}

export function useUpdateModelPricing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: number; patch: JsonBody }) =>
      fetchJson(`/api/admin/pricing/models/${args.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(args.patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/admin/pricing/models"] }),
  });
}

export function useModelUsageHistory(id: number | null, days = 30) {
  return useQuery({
    queryKey: ["/api/admin/pricing/models", id, "usage", days],
    queryFn: () => fetchJson<{ days: number; summary: any; recent: any[] }>(`/api/admin/pricing/models/${id}/usage?days=${days}`),
    enabled: !!id,
  });
}

export function useCreateModelPricing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: JsonBody) =>
      fetchJson("/api/admin/pricing/models", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/admin/pricing/models"] }),
  });
}

// ─── Plans ──────────────────────────────────────────────────────────────

export function useModelPlans() {
  return useQuery({
    queryKey: ["/api/admin/pricing/plans"],
    queryFn: () => fetchJson<{ rows: any[] }>("/api/admin/pricing/plans"),
  });
}

export function useUpdateModelPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: number; patch: JsonBody }) =>
      fetchJson(`/api/admin/pricing/plans/${args.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(args.patch),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/admin/pricing/plans"] }),
  });
}

export function useCreateModelPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: JsonBody) =>
      fetchJson("/api/admin/pricing/plans", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/admin/pricing/plans"] }),
  });
}

export function useArchiveModelPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      fetchJson(`/api/admin/pricing/plans/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/admin/pricing/plans"] }),
  });
}
